import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { LOTTERY_TYPES } from "../../../config/lotteryConfig";
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

// 「近い過去回」をニア検索RPCから取得（qccは対象RPCなし）
async function fetchNearRounds(lotteryKey, row) {
  const config = LOTTERY_TYPES[lotteryKey];
  if (!config || lotteryKey === "qcc") return null;

  if (config.type === "loto") {
    const nums = row.main_number.split(",").map(Number);
    const bonus = row.bonus_number ? row.bonus_number.split(",").map(Number) : null;
    const { data, error } = await supabase
      .schema("chatorahack")
      .rpc("get_numlot_related_lt", {
        p_lottery_key: lotteryKey,
        p_numbers: nums,
        p_bonus: bonus,
        p_exclude_round: row.round,
        p_max_round: row.round - 1,
        p_limit: 5,
      });
    if (error) throw error;
    return { type: "loto", rows: data || [] };
  }

  if (config.type === "numbers") {
    const { data, error } = await supabase
      .schema("chatorahack")
      .rpc("get_numlot_related_nm", {
        p_lottery_key: lotteryKey,
        p_number: row.main_number,
        p_exclude_round: row.round,
        p_max_round: row.round - 1,
        p_limit: 5,
      });
    if (error) throw error;
    return { type: "numbers", rows: data || [] };
  }

  if (config.type === "bingo") {
    const cells = row.main_number.split(",").map(Number);
    const { data, error } = await supabase
      .schema("chatorahack")
      .rpc("get_numlot_related_bg", {
        p_numbers: cells,
        p_exclude_round: row.round,
        p_max_round: row.round - 1,
        p_limit: 5,
      });
    if (error) throw error;
    return { type: "bingo", rows: data || [] };
  }

  return null;
}

export default function LotteryNumlotDetailContainer({ lotteryKey, Template }) {
  const [data, setData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [related, setRelated] = useState(null);
  const [loading, setLoading] = useState(true);
  const { round } = useParams();

  useEffect(() => {
    const config = LOTTERY_TYPES[lotteryKey];

    if (!round || !config) {
      setLoading(false);
      return;
    }

    const fetchDetail = async () => {
      NProgress.start();
      setLoading(true);
      setRelated(null);
      try {
        const [maxRoundRes, detailRes] = await Promise.all([
          supabase
            .schema('chatorahack')
            .from(config.tableName)
            .select('round')
            .order('round', { ascending: false })
            .limit(1)
            .single(),
          supabase
            .schema('chatorahack')
            .rpc('get_lottery_numlot_detail', {
              p_lottery_key: lotteryKey,
              p_round: parseInt(round, 10)
            }),
        ]);

        if (detailRes.error) throw detailRes.error;
        const row = detailRes.data[0];
        if (!row) throw new Error("データなし");

        const rowData = {
          ...row,
          is_latest: row.round === maxRoundRes.data?.round,
          lotteryKey: lotteryKey,
        };
        setData(rowData);

        // AI解析データを取得（lottery_uuidで紐付け）
        const { data: aiData } = await supabase
          .schema('chatorahack')
          .from('lottery_numlot_analysis_ai')
          .select('result, ai_model, updated_at')
          .eq('lottery_uuid', row.uuid)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (aiData?.result) {
          const result = typeof aiData.result === 'string'
            ? JSON.parse(aiData.result)
            : aiData.result;
          setAiAnalysis({ ...result, ai_model: aiData.ai_model, updated_at: aiData.updated_at });
        }

        // 近い過去回（取得失敗・0件時はセクション非表示。詳細表示はブロックしない）
        try {
          const near = await fetchNearRounds(lotteryKey, row);
          if (near?.rows?.length) setRelated(near);
        } catch (nearErr) {
          setRelated(null);
        }
      } catch (err) {
        setData(null);
      } finally {
        setLoading(false);
        NProgress.done();
      }
    };

    fetchDetail();
  }, [lotteryKey, round]);

  const currentConfig = LOTTERY_TYPES[lotteryKey];
  return <Template data={data} config={currentConfig} loading={loading} aiAnalysis={aiAnalysis} related={related} />;
}
